/** Wizard step labels (BLS-39). */
export const BLS_STEPS = [
  { id: 1, label: 'Survey setup' },
  { id: 2, label: 'Audience & delivery' },
  { id: 3, label: 'Measurement mode' },
  { id: 4, label: 'Validate & launch' },
];

export const BLS_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
};

export const BLS_MODE = {
  STANDARD: 'standard',
  OPTIMIZED: 'optimized',
};

export const DURATION_UNITS = ['minutes', 'hours', 'days'];
export const TEST_PERIOD_UNITS = ['days', 'weeks'];
export const PLACEMENT_SIZES = [
  { id: '970x250', label: 'Billboard', width: 970, height: 250 },
  { id: '300x600', label: 'Half Page', width: 300, height: 600 },
  { id: '300x250', label: 'Medium Rectangle', width: 300, height: 250 },
  { id: '728x90', label: 'Leaderboard', width: 728, height: 90 },
];

export const FREQUENCY_PRESETS = [
  { id: '1-3-5', label: '1, 3, 5', buckets: [1, 3, 5] },
  { id: '2-4-6', label: '2, 4, 6', buckets: [2, 4, 6] },
  { id: '4-6-8', label: '4, 6, 8', buckets: [4, 6, 8] },
  { id: '3-5-7', label: '3, 5, 7', buckets: [3, 5, 7] },
  { id: 'custom', label: 'Custom', buckets: [] },
];

export const FORECAST_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  PASSED: 'passed',
  FAILED: 'failed',
};

export const ELIGIBILITY = {
  ELIGIBLE: 'eligible',
  LOW_FORECAST: 'low_forecast',
  MISSING_ID: 'missing_id',
  INACTIVE: 'inactive',
};

export const MEDIA_TYPE = {
  DISPLAY: 'Display',
  VIDEO: 'Video/VAST',
};

export const SURVEY_SOURCE = {
  BUILD: 'build',
  UPLOAD: 'upload',
};

const EXTRA_STORAGE_KEY = 'bls-wizard-extras';

export function createDefaultWizardConfig(campaign) {
  const brand = campaign?.brand || '';
  const name = campaign?.name ? `${campaign.name} Brand Lift` : 'Brand Lift Survey';
  return {
    id: null,
    status: BLS_STATUS.DRAFT,
    name,
    brand,
    campaignId: campaign?.id || null,
    campaignName: campaign?.name || '',
    surveySource: SURVEY_SOURCE.BUILD,
    brandMetric: '',
    questionActionWord: '',
    questionWording: '',
    answerCount: 5,
    answerOptions: [],
    positiveAnswerIndex: 0,
    clickToSubmit: false,
    htmlFile: null,
    htmlFileName: '',
    controlSplit: 15,
    exposureFrequency: 3,
    askLimit: 2,
    returnFrequency: 2,
    displayWindowMin: 3,
    displayWindowMinUnit: 'minutes',
    displayWindowMax: 7,
    displayWindowMaxUnit: 'days',
    blsMode: null,
    selectedParentIds: [],
    selectedSetIds: [],
    frequencyTesting: {
      enabled: false,
      preset: '1-3-5',
      buckets: [1, 3, 5],
    },
    forecast: {
      minImpressions: 10000,
      status: FORECAST_STATUS.IDLE,
      failedSetIds: [],
    },
    minPerformanceLead: 10,
    performersToPromote: 1,
    winningInventoryPercent: 80,
    minTestPeriod: 7,
    minTestPeriodUnit: 'days',
    minTestVolume: 1000,
    startOptimizationWhen: 'both',
    flightPromotion: 'always-on',
  };
}

export function durationToSeconds(value, unit) {
  const n = Number(value) || 0;
  if (unit === 'minutes') return n * 60;
  if (unit === 'hours') return n * 3600;
  return n * 86400;
}

export function durationToDays(value, unit) {
  const seconds = durationToSeconds(value, unit);
  return Math.max(1, Math.round(seconds / 86400));
}

export function daysToSeconds(days) {
  return (Number(days) || 0) * 86400;
}

export function loadExtrasMap() {
  try {
    const raw = localStorage.getItem(EXTRA_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

export function saveExtrasMap(map) {
  try {
    localStorage.setItem(EXTRA_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    // Quota / private mode — Vuex still holds the extras for the session.
  }
}

export function pickExtrasFromConfig(config) {
  return {
    brandMetric: config.brandMetric,
    questionActionWord: config.questionActionWord,
    questionWording: config.questionWording,
    answerCount: config.answerCount,
    answerOptions: config.answerOptions,
    positiveAnswerIndex: config.positiveAnswerIndex,
    clickToSubmit: config.clickToSubmit,
    htmlFileName: config.htmlFileName,
    surveySource: config.surveySource,
    displayWindowMin: config.displayWindowMin,
    displayWindowMinUnit: config.displayWindowMinUnit,
    displayWindowMax: config.displayWindowMax,
    displayWindowMaxUnit: config.displayWindowMaxUnit,
    blsMode: config.blsMode,
    selectedParentIds: config.selectedParentIds,
    selectedSetIds: config.selectedSetIds,
    frequencyTesting: config.frequencyTesting,
    forecast: config.forecast,
    minPerformanceLead: config.minPerformanceLead,
    performersToPromote: config.performersToPromote,
    winningInventoryPercent: config.winningInventoryPercent,
    minTestPeriod: config.minTestPeriod,
    minTestPeriodUnit: config.minTestPeriodUnit,
    minTestVolume: config.minTestVolume,
    startOptimizationWhen: config.startOptimizationWhen,
    flightPromotion: config.flightPromotion,
    pausedAt: config.pausedAt || null,
    name: config.name,
    brand: config.brand,
    controlSplit: config.controlSplit,
    exposureFrequency: config.exposureFrequency,
    askLimit: config.askLimit,
    returnFrequency: config.returnFrequency,
  };
}
